import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input, type InputProps } from './input';
import { cn } from '@/lib/utils';

/**
 * Wraps Input + an eye/eye-off toggle for visibility. Default = hidden
 * (`type="password"`); click the icon to reveal as plain text. Reveal
 * state is local component state — it resets if the component remounts,
 * so closing/reopening a dialog re-hides the value, which is what we want.
 *
 * Drop-in for `<Input type="password" ... />` — accepts all the same
 * props because we just spread them onto the inner Input.
 */

type Props = Omit<InputProps, 'type'>;

export const PasswordInput = React.forwardRef<HTMLInputElement, Props>(function PasswordInput(
  { className, ...rest },
  ref,
) {
  const [reveal, setReveal] = React.useState(false);
  return (
    <div className="relative">
      <Input
        {...rest}
        ref={ref}
        type={reveal ? 'text' : 'password'}
        // Pad the right so the toggle button doesn't overlap typed text.
        className={cn('pr-10', className)}
      />
      <button
        type="button"
        onClick={() => setReveal((v) => !v)}
        aria-label={reveal ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
        aria-pressed={reveal}
        tabIndex={-1}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 transition-colors hover:text-slate-700 dark:hover:text-slate-200"
      >
        {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
});
